import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import './admin-panel.css';

const MAX_IMAGES = 5;
const MIN_IMAGES = 1;

const emptyForm = {
  title: '',
  location: '',
  duration: '',
  persons: '',
  price: '',
  description: '',
  rating: 5,
};

export default function CreatePackageTab() {
  const location = useLocation();
  const navigate = useNavigate();
  const editId = location.state?.editId;

  const [form, setForm] = useState(emptyForm);
  const [images, setImages] = useState([]); // [{ file, previewUrl }]
  const [existingImages, setExistingImages] = useState([]); // image URLs already on the package being edited
  const [included, setIncluded] = useState([]);
  const [includedInput, setIncludedInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(Boolean(editId));

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;

    axios
      .get('/api/packages/', { params: { status: 'all' } })
      .then((response) => {
        if (cancelled) return;
        const pkg = (response.data?.packages ?? []).find((p) => p.id === editId);
        if (!pkg) {
          toast.error('Package not found.');
          return;
        }
        setForm({
          title: pkg.title || '',
          location: pkg.location || '',
          duration: pkg.duration || '',
          persons: pkg.persons || '',
          price: pkg.price || '',
          description: pkg.description || '',
          rating: pkg.rating || 5,
        });
        setIncluded(pkg.included || []);
        setExistingImages(pkg.images || []);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load package for editing.');
      })
      .finally(() => {
        if (!cancelled) setLoadingEdit(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editId]);

  useEffect(() => {
    return () => {
      images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, [images]);

  const totalImageCount = images.length + existingImages.length;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remaining = MAX_IMAGES - totalImageCount;
    if (remaining <= 0) {
      toast.error(`You can upload up to ${MAX_IMAGES} images.`);
      e.target.value = '';
      return;
    }

    const filesToAdd = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.info(`Only ${remaining} more image(s) could be added (max ${MAX_IMAGES}).`);
    }
    e.target.value = '';

    const newImages = filesToAdd.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setImages((prev) => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const addIncluded = () => {
    const value = includedInput.trim();
    if (!value) return;
    setIncluded((prev) => [...prev, value]);
    setIncludedInput('');
  };

  const removeIncluded = (index) => {
    setIncluded((prev) => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setForm(emptyForm);
    images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    setImages([]);
    setExistingImages([]);
    setIncluded([]);
    setIncludedInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Please provide a title and description.');
      return;
    }

    if (editId) {
      // Editing only updates text fields today — image changes on an existing
      // package aren't supported by the API yet.
      setSubmitting(true);
      try {
        await axios.post(`/api/packages/?id=${editId}&_method=PATCH`, {
          ...form,
          status: 'review',
        });
        toast.success('Package updated and sent back for review.');
        navigate('/adminpanel/review');
      } catch {
        toast.error('Could not update the package. Please try again.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (images.length < MIN_IMAGES) {
      toast.error(`Please add at least ${MIN_IMAGES} image.`);
      return;
    }

    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => payload.append(key, value));
    included.forEach((item) => payload.append('included[]', item));
    images.forEach((img) => payload.append('images[]', img.file));

    setSubmitting(true);
    try {
      await axios.post('/api/packages/', payload);
      toast.success('Package submitted for review.');
      resetForm();
    } catch (err) {
      const message = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : 'Could not submit the package. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingEdit) {
    return (
      <div>
        <div className="admin-topbar">
          <div>
            <h1>Edit Package</h1>
            <p>Loading package…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-topbar">
        <div>
          <h1>{editId ? 'Edit Package' : 'Create Package'}</h1>
          <p>
            {editId
              ? 'Update the package details below. Saving will send it back to Review.'
              : 'Fill in the package details. New packages are sent to the Review tab.'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="admin-panel">
          <h3 className="admin-panel-title">
            <i className="fa fa-info-circle" /> Basic Details
          </h3>
          <div className="admin-grid-2">
            <div className="admin-field">
              <label>Package Title</label>
              <input
                type="text"
                placeholder="Package Title"
                value={form.title}
                onChange={handleChange('title')}
                required
              />
            </div>
            <div className="admin-field">
              <label>Location</label>
              <input
                type="text"
                placeholder="Location"
                value={form.location}
                onChange={handleChange('location')}
                required
              />
            </div>
          </div>
          <div className="admin-grid-3">
            <div className="admin-field">
              <label>Duration (days)</label>
              <input
                type="text"
                placeholder="e.g. 12 days"
                value={form.duration}
                onChange={handleChange('duration')}
                required
              />
            </div>
            <div className="admin-field">
              <label>Persons</label>
              <input
                type="text"
                placeholder="e.g. 1 Person"
                value={form.persons}
                onChange={handleChange('persons')}
                required
              />
            </div>
            <div className="admin-field">
              <label>Price (optional)</label>
              <input
                type="text"
                placeholder="e.g. PKR 250,000"
                value={form.price}
                onChange={handleChange('price')}
              />
            </div>
          </div>
          <div className="admin-field">
            <label>Description</label>
            <textarea
              rows={4}
              placeholder="Describe the package experience..."
              value={form.description}
              onChange={handleChange('description')}
              required
            />
          </div>
          <div className="admin-field">
            <label>Rating</label>
            <div className="admin-star-input">
              {[1, 2, 3, 4, 5].map((star) => (
                <i
                  key={star}
                  className={`fa fa-star${star <= form.rating ? ' filled' : ''}`}
                  onClick={() => setForm((prev) => ({ ...prev, rating: star }))}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="admin-panel">
          <h3 className="admin-panel-title">
            <i className="fa fa-image" /> Package Images
          </h3>
          <p className="admin-field-hint">
            {editId
              ? 'Images cannot be changed while editing yet.'
              : `Add up to ${MAX_IMAGES} images (at least ${MIN_IMAGES}). ${totalImageCount}/${MAX_IMAGES} added.`}
          </p>

          {existingImages.length > 0 && (
            <div className="admin-image-thumb-grid">
              {existingImages.map((src, index) => (
                <div className="admin-image-thumb" key={src}>
                  <img src={src} alt={`Package ${index + 1}`} />
                  {index === 0 && <span className="admin-image-thumb-cover">Cover</span>}
                </div>
              ))}
            </div>
          )}

          {!editId && images.length > 0 && (
            <div className="admin-image-thumb-grid">
              {images.map((img, index) => (
                <div className="admin-image-thumb" key={img.previewUrl}>
                  <img src={img.previewUrl} alt={`Package ${index + 1}`} />
                  {index === 0 && existingImages.length === 0 && (
                    <span className="admin-image-thumb-cover">Cover</span>
                  )}
                  <button
                    type="button"
                    className="admin-image-thumb-remove"
                    onClick={() => removeImage(index)}
                    title="Remove image"
                  >
                    <i className="fa fa-times" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {!editId && totalImageCount < MAX_IMAGES && (
            <label className="admin-image-drop admin-image-drop-compact">
              <div className="admin-image-drop-icon">
                <i className="fa fa-cloud-upload-alt" />
              </div>
              <div>Click to upload package images</div>
              <div style={{ fontSize: 12, color: 'var(--admin-text-muted)', marginTop: 4 }}>
                JPG or PNG · up to {MAX_IMAGES - totalImageCount} more
              </div>
              <input type="file" accept="image/*" multiple onChange={handleImagesChange} />
            </label>
          )}
        </div>

        <div className="admin-panel">
          <h3 className="admin-panel-title">
            <i className="fa fa-check-circle" /> What's Included
          </h3>
          <div className="admin-inline-add">
            <input
              type="text"
              placeholder="e.g. Return flights"
              value={includedInput}
              onChange={(e) => setIncludedInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addIncluded();
                }
              }}
            />
            <button type="button" className="admin-btn admin-btn-outline" onClick={addIncluded}>
              Add
            </button>
          </div>
          {included.length > 0 && (
            <div className="admin-tag-list">
              {included.map((item, index) => (
                <span className="admin-tag" key={`${item}-${index}`}>
                  {item}
                  <button type="button" onClick={() => removeIncluded(index)}>
                    <i className="fa fa-times" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={submitting}>
            <i className="fa fa-paper-plane" /> {editId ? 'Save Changes' : 'Submit for Review'}
          </button>
          <button type="button" className="admin-btn admin-btn-outline" onClick={resetForm} disabled={submitting}>
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}
